import React, { FunctionComponent, useContext, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { DateTime } from 'luxon'
import { decode } from 'he'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { get, pick, isEmpty } from 'lodash'
import accounting from 'accounting'
import { rebuild } from 'react-tooltip'

import {
  DateTimeDisplay,
  PageDescription,
  PageHeading,
  Tabs,
} from '../../../../components'
import Table from '../../../../components/Table/next'
import ReportActions from '../../../../components/ReportActions'
import Summary from '../Summary'

import { getDefinition } from '../../../../utils/dictionary'
import { pruneObject } from '../../../../utils/objectManagement'
import { State } from '../../../../reducers'
import {
  getFilteredPayments,
  exportPayments,
} from '../../../../actions/payments'
import { OutstandingBalanceTableContext } from './index'

import {
  PaymentsData,
  PaymentSummary,
  Payment,
} from '../../../../typings/payments'
import { ReportStatus } from '../../../../typings/report'

import styles from '../styles.module.css'

const OutstandingBalanceTable: FunctionComponent = () => {
  const dispatch = useDispatch()

  const {
    sort,
    pagination: { page, size },
    changePage,
    changePageSize,
    changeSort,
  } = useContext(OutstandingBalanceTableContext)

  const outstandingBalanceLoading = useSelector<State, boolean>(
    state => get(state, 'payments.outstandingBalance.status') === 'fetching'
  )

  const outstandingBalance = useSelector<State, PaymentsData>(
    state => state.payments.outstandingBalance
  )

  const outstandingBalanceSummary = useSelector<State, PaymentSummary>(state =>
    get(state, 'payments.outstandingBalance.summary', {})
  )

  const exportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.payments.outstandingBalance.export, [
      'hasData',
      'status',
      'error',
    ]),
    loading: state.payments.outstandingBalance.export.status === 'fetching',
    hasError: !isEmpty(state.payments.outstandingBalance.export.error),
  }))

  useEffect(() => {
    dispatch(
      getFilteredPayments('outstandingBalance', {
        page,
        size,
        ...pruneObject(sort),
      })
    )
  }, [sort, page, size])

  useEffect(() => {
    rebuild()
  })

  return (
    <>
      <PageHeading title="Payments">
        <Tabs
          items={[
            {
              title: 'Outstanding Balance',
              isSelected: true,
              to: './outstanding-balance',
            },
            {
              title: 'Payment History',
              isSelected: false,
              to: './history',
            },
          ]}
        />

        <ReportActions
          className={styles.actions}
          fetchExport={() =>
            dispatch(
              exportPayments('outstandingBalance', null, pruneObject(sort))
            )
          }
          exportStatus={exportStatus}
        />
      </PageHeading>

      <PageDescription content="This report reflects all the commissions to be paid in the next payment." />

      <Summary
        isLoading={outstandingBalanceLoading}
        data={outstandingBalanceSummary}
      />

      <Table
        className={styles.table}
        isLoading={outstandingBalanceLoading}
        onSortChange={changeSort}
        sort={sort}
        data={outstandingBalance.transactions as Payment[]}
        pagination={{
          pageSize: size,
          currentPage: page,
          total: get(outstandingBalance, 'pagination.total', 1),
          onPaginate: changePage,
          onPageSizeUpdate: changePageSize,
        }}
        cols={[
          {
            text: 'Transaction ID',
            field: 'transactionId',
          },
          {
            text: 'Process Date',
            field: 'processDate',
            sortable: true,
            render: ({ processDate }) => (
              <DateTimeDisplay datetime={DateTime.fromSQL(processDate)} />
            ),
          },
          {
            text: 'Product',
            field: 'productName',
            sortable: true,
            render: ({ productName }) => decode(productName),
          },
          {
            text: 'Advertiser',
            field: 'advertiserName',
            sortable: true,
          },
          {
            text: 'Commission',
            field: 'commission',
            sortable: true,
            className: styles.right,
            render: ({ commission }) => accounting.formatMoney(commission, '$'),
          },
          {
            text: 'Adjustments',
            className: styles.right,
            render: ({ adjustments }) =>
              accounting.formatMoney(adjustments, '$'),
          },
          {
            text: 'Total Amount',
            className: styles.right,
            render: ({ total }) => accounting.formatMoney(total, '$'),
          },
          {
            text: 'Cross Sale',
            className: styles.crossSale,
            render: row =>
              row.isCrossSale && (
                <div
                  data-tip={getDefinition('cross sale')}
                  data-for="global-tooltip"
                >
                  <FontAwesomeIcon
                    icon={['fas', 'expand-arrows-alt']}
                    className={styles.icon}
                  />
                </div>
              ),
          },
        ]}
      />
    </>
  )
}

export default OutstandingBalanceTable
